import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES, changeLanguage, type SupportedLanguage } from '../i18n';

interface LanguageSwitcherProps {
  /** Variant style */
  variant?: 'dropdown' | 'buttons' | 'minimal' | 'minimalOnDark' | 'yimTalkPill' | 'toggle';
  /** Show flag icons */
  showFlags?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Language Switcher Component
 * Allows users to change the application language
 */
export function LanguageSwitcher({
  variant = 'dropdown',
  showFlags = true,
  className = '',
}: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language as SupportedLanguage;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    await changeLanguage(lang);
    setIsOpen(false);
  };

  // Button variant - shows all languages as buttons
  if (variant === 'buttons') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
          <button
            key={code}
            onClick={() => handleLanguageChange(code as SupportedLanguage)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              currentLang === code
                ? 'bg-primary-100 text-primary-700'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            title={lang.name}
          >
            {showFlags && <span className="mr-1">{lang.flag}</span>}
            {code.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  // Toggle variant - beautiful sliding switch
  if (variant === 'toggle') {
    const languages = Object.entries(SUPPORTED_LANGUAGES);
    const currentIndex = languages.findIndex(([code]) => code === currentLang);
    
    return (
      <div className={`inline-flex items-center bg-slate-100 rounded-full p-0.5 border border-slate-200 ${className}`}>
        <div className="relative flex">
          {/* Sliding background */}
          <div
            className="absolute inset-y-0.5 bg-gradient-to-br from-[#1E40AF] to-blue-600 rounded-full shadow-md shadow-blue-500/20 transition-all duration-300 ease-out"
            style={{
              width: `calc(${100 / languages.length}% - 0.125rem)`,
              left: `calc(${currentIndex * (100 / languages.length)}% + 0.0625rem)`,
            }}
          />
          
          {/* Language buttons */}
          {languages.map(([code, lang]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code as SupportedLanguage)}
              className={`relative z-10 px-2.5 py-1 text-xs font-bold rounded-full transition-colors duration-300 min-w-[38px] ${
                currentLang === code
                  ? 'text-white'
                  : 'text-slate-600 hover:text-slate-700'
              }`}
              title={lang.name}
            >
              {code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'yimTalkPill') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-9 items-center gap-1.5 rounded-full border-2 border-white/50 bg-white/20 px-3 text-xs font-bold uppercase tracking-wide text-white shadow-sm hover:bg-white/30 focus-visible:ring-2 focus-visible:ring-accent-400"
          aria-label={t('language.select')}
        >
          <Globe className="h-4 w-4 shrink-0" aria-hidden />
          <span>{currentLang}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
        {isOpen && (
          <div className="absolute right-0 top-full z-50 mt-2 min-w-[140px] rounded-xl border border-white/20 bg-primary-900 py-1 shadow-xl">
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
              <button
                key={code}
                type="button"
                onClick={() => handleLanguageChange(code as SupportedLanguage)}
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-white/10 ${
                  currentLang === code ? 'font-bold text-accent-300' : 'text-white'
                }`}
              >
                {showFlags && <span>{lang.flag}</span>}
                <span>{lang.nativeName}</span>
                {currentLang === code && <Check className="ml-auto h-4 w-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Minimal on dark backgrounds (YIM Talk, hero sections)
  if (variant === 'minimalOnDark') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-9 w-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/50"
          aria-label={t('language.select')}
        >
          <Globe className="h-4 w-4" />
        </button>
        {isOpen && (
          <div className="absolute right-0 top-full z-50 mt-2 min-w-[130px] rounded-xl border border-white/10 bg-slate-900/95 py-1 shadow-xl backdrop-blur-md">
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
              <button
                key={code}
                type="button"
                onClick={() => handleLanguageChange(code as SupportedLanguage)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/10 ${
                  currentLang === code ? 'font-medium text-accent-300' : 'text-white/80'
                }`}
              >
                {showFlags && <span>{lang.flag}</span>}
                <span>{lang.nativeName}</span>
                {currentLang === code && <Check className="ml-auto h-4 w-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Minimal variant - just the language code
  if (variant === 'minimal') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
        >
          <Globe className="w-4 h-4" />
          <span className="uppercase">{currentLang}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-1 py-1 bg-white rounded-lg shadow-lg border border-slate-200 min-w-[120px] z-50">
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code as SupportedLanguage)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 ${
                  currentLang === code ? 'text-primary-600 font-medium' : 'text-slate-700'
                }`}
              >
                {showFlags && <span>{lang.flag}</span>}
                <span>{lang.nativeName}</span>
                {currentLang === code && <Check className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        aria-label={t('language.select')}
      >
        <Globe className="w-4 h-4" />
        {showFlags && <span>{SUPPORTED_LANGUAGES[currentLang]?.flag}</span>}
        <span>{SUPPORTED_LANGUAGES[currentLang]?.nativeName || currentLang}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 py-1 bg-white rounded-lg shadow-lg border border-slate-200 min-w-[160px] z-50 animate-fade-in">
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code as SupportedLanguage)}
              className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                currentLang === code
                  ? 'text-primary-600 font-medium bg-primary-50'
                  : 'text-slate-700'
              }`}
            >
              {showFlags && <span className="text-base">{lang.flag}</span>}
              <span className="flex-1">{lang.nativeName}</span>
              {currentLang === code && <Check className="w-4 h-4 text-primary-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;

